/**
 * CL-ENGINEER-LAUNCH-001 — One external-portal photo visibility rule (pure).
 *
 * Canonical field today: `clientVisible` (legacy “planning” flag).
 * Hidden from project portal ⇒ hidden from Client, Engineer, Designer, and Planning photos.
 * Admin always retains management visibility.
 */

function safeStr(value) {
  return String(value ?? "").trim();
}

/**
 * @returns {{
 *   isAdminVisible: boolean,
 *   isPortalVisible: boolean,
 *   hiddenReason: string,
 *   legacyVisibilitySource: string,
 *   usesLegacyPlanningFlag: boolean,
 * }}
 */
export function resolveProjectPhotoPortalVisibility(file = {}) {
  const status = safeStr(file?.status || "active").toLowerCase();
  if (status === "deleted") {
    return {
      isAdminVisible: false,
      isPortalVisible: false,
      hiddenReason: "deleted",
      legacyVisibilitySource: "status",
      usesLegacyPlanningFlag: true,
    };
  }

  const visibility = safeStr(file?.visibility).toLowerCase();
  if (visibility === "admin" || visibility === "internal") {
    return {
      isAdminVisible: true,
      isPortalVisible: false,
      hiddenReason: "visibility_admin_internal",
      legacyVisibilitySource: "visibility",
      usesLegacyPlanningFlag: true,
    };
  }

  // Explicit projectFileAudience admin_only wins when present.
  const audience = safeStr(file?.projectFileAudience || file?.fileAudience).toLowerCase();
  if (audience === "admin_only") {
    return {
      isAdminVisible: true,
      isPortalVisible: false,
      hiddenReason: "projectFileAudience_admin_only",
      legacyVisibilitySource: "projectFileAudience",
      usesLegacyPlanningFlag: false,
    };
  }

  if (file?.clientVisible === false) {
    return {
      isAdminVisible: true,
      isPortalVisible: false,
      hiddenReason: "hidden_from_project_portal",
      legacyVisibilitySource: "clientVisible",
      usesLegacyPlanningFlag: true,
    };
  }

  return {
    isAdminVisible: true,
    isPortalVisible: true,
    hiddenReason: "",
    legacyVisibilitySource: "clientVisible" in (file || {}) ? "clientVisible" : "default_visible",
    usesLegacyPlanningFlag: true,
  };
}

export function isProjectPhotoPortalVisible(file = {}) {
  return resolveProjectPhotoPortalVisibility(file).isPortalVisible === true;
}

export function isProjectPhotoAdminVisible(file = {}) {
  return resolveProjectPhotoPortalVisibility(file).isAdminVisible === true;
}

export const PROJECT_PHOTO_PORTAL_VISIBILITY_COPY = Object.freeze({
  sectionTitle: "Photo visibility",
  visibleLabel: "Visible in project portal",
  visibleHelp: "Clients and collaborators with Photos access can view this photo.",
  hiddenLabel: "Hidden from project portal",
  hiddenHelp: "The photo remains available to Admin only.",
  badgeVisible: "Visible in project portal",
  badgeHidden: "Hidden from project portal",
  actionHide: "Hide from portal",
  actionShow: "Show in portal",
  noticeShown: "Photo is visible in the project portal.",
  noticeHidden: "Photo hidden from the project portal.",
});
